import { authenticatedFetch } from "./http";

export interface PreparationTask {
  id: string;
  taskNumber: string;
  orderId: string;
  orderNumber: string;
  itemCode: string;
  itemName: string;
  handlingUnitId: string;
  handlingUnitNumber: string;
  sourceStorageBinId: string;
  sourceLocationCode: string;
  destinationProcessPointId: string;
  destinationProcessPointCode: string;
  routeNumber: string | null;
  routeSequence: number | null;
  sourceTravelSequence: number | null;
  requestedQuantity: number;
  pickedQuantity: number;
  uom: string;
  status: "OPEN" | "IN_PROGRESS" | "COMPLETED" | "EXCEPTION" | "CANCELLED";
  priority: number;
  assignedUserDisplayName: string | null;
  exceptionCode: string | null;
  exceptionComment: string | null;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
  outputHandlingUnitId: string | null;
  outputHandlingUnitNumber: string | null;
  sourceQuantityBeforePicking: number | null;
  sourceQuantityRemaining: number | null;
  derogatedLotVisualCheckRequired: boolean;
}

export interface PreparationOrder {
  id: string;
  orderNumber: string;
  allocationId: string;
  allocationReference: string;
  itemCode: string;
  itemName: string;
  warehouseId: string;
  warehouseCode: string;
  destinationProcessPointCode: string;
  outboundOrderId: string | null;
  outboundOrderNumber: string | null;
  outboundOrderLineNumber: number | null;
  outboundExternalReference: string | null;
  recipientClientCode: string | null;
  recipientClientName: string | null;
  deliveryDestinationCode: string | null;
  deliveryDestinationName: string | null;
  requestedDeliveryFrom: string | null;
  requestedDeliveryTo: string | null;
  status: "OPEN" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED" | "EXCEPTION";
  priority: number;
  createdAt: string;
  version: number;
  tasks: PreparationTask[];
}

export function loadPreparations(): Promise<PreparationOrder[]> {
  return authenticatedFetch<PreparationOrder[]>("/api/v1/preparations");
}

export function createPreparation(
  allocationId: string, destinationProcessPointId: string, priority: number
): Promise<PreparationOrder> {
  return authenticatedFetch<PreparationOrder>("/api/v1/preparations", {
    method: "POST",
    body: JSON.stringify({ allocationId, destinationProcessPointId, priority })
  });
}

export interface PreparationWaveStep {
  id: string;
  preparationTaskId: string;
  taskNumber: string;
  orderNumber: string;
  handlingUnitNumber: string;
  itemCode: string;
  sequenceNumber: number;
  sourceLocationCode: string;
  sourceTravelSequence: number | null;
  requestedQuantity: number;
  uom: string;
  status: string;
  distanceFromPrevious: number;
}

export interface PreparationWave {
  id: string;
  waveNumber: string;
  warehouseId: string;
  warehouseCode: string;
  clientCode: string | null;
  destinationProcessPointCode: string;
  status: string;
  priority: number;
  departureTravelSequence: number | null;
  estimatedDistanceUnits: number;
  returnDistanceUnits: number;
  groupingReasons: string[];
  preparationOrderIds: string[];
  orderNumbers: string[];
  createdAt: string;
  version: number;
  handlingEquipmentId: string;
  handlingEquipmentNumber: string;
  handlingEquipmentTypeCode: string;
  handlingEquipmentTypeName: string;
  steps: PreparationWaveStep[];
}

export interface PickingHandlingEquipment {
  id: string;
  equipmentNumber: string;
  typeCode: string;
  typeName: string;
  maxLoadKg: number | null;
  requiredLoadKg: number;
  suitable: boolean;
  suitabilityMessage: string;
}

export interface PickingRouteStep {
  id: string;
  preparationTaskId: string;
  taskNumber: string;
  handlingUnitNumber: string;
  itemCode: string;
  itemName: string;
  sequenceNumber: number;
  sourceLocationCode: string;
  sourceTravelSequence: number | null;
  aisle: string | null;
  bay: string | null;
  level: string | null;
  position: string | null;
  requestedQuantity: number;
  uom: string;
  status: string;
  distanceFromPrevious: number;
}

export interface PickingRoute {
  id: string;
  preparationOrderId: string;
  routeNumber: string;
  warehouseCode: string;
  strategy: string;
  status: string;
  priority: number;
  estimatedDistanceUnits: number;
  departureProcessPointCode: string | null;
  departureTravelSequence: number | null;
  returnDistanceUnits: number;
  algorithmVersion: string;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  version: number;
  steps: PickingRouteStep[];
}

export function loadPickingRoute(orderId: string): Promise<PickingRoute> {
  return authenticatedFetch<PickingRoute>(
    `/api/v1/preparations/${orderId}/route`
  );
}

export function loadPreparationWaves(): Promise<PreparationWave[]> {
  return authenticatedFetch<PreparationWave[]>("/api/v1/preparations/waves");
}

export function createPreparationWave(
  preparationOrderIds: string[], priority: number,
  handlingEquipmentId: string | null,
  handlingEquipmentIdentifier: string | null = null
): Promise<PreparationWave> {
  return authenticatedFetch<PreparationWave>("/api/v1/preparations/waves", {
    method: "POST",
    body: JSON.stringify({ preparationOrderIds, priority, handlingEquipmentId,
      handlingEquipmentIdentifier })
  });
}

export function loadPreparationWaveHandlingEquipment(
  preparationOrderIds: string[]
): Promise<PickingHandlingEquipment[]> {
  return authenticatedFetch<PickingHandlingEquipment[]>(
    "/api/v1/preparations/waves/handling-equipment",
    { method: "POST", body: JSON.stringify({ preparationOrderIds }) }
  );
}

export function takePreparationWave(
  waveId: string, version: number
): Promise<PreparationWave> {
  return authenticatedFetch<PreparationWave>(
    `/api/v1/preparations/waves/${waveId}/take`,
    { method: "POST", body: JSON.stringify({ version }) }
  );
}

export function takePreparationTask(
  taskId: string, version: number, handlingEquipmentId: string | null,
  noEquipmentRequired: boolean, handlingEquipmentIdentifier: string | null = null
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/take`,
    { method: "POST", body: JSON.stringify({
      version, handlingEquipmentId, handlingEquipmentIdentifier,
      noEquipmentRequired
    }) }
  );
}

export function loadPickingHandlingEquipment(
  taskId: string
): Promise<PickingHandlingEquipment[]> {
  return authenticatedFetch<PickingHandlingEquipment[]>(
    `/api/v1/preparations/tasks/${taskId}/handling-equipment`
  );
}

export function completePreparationTask(
  taskId: string, request: Record<string, unknown>
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/complete`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function prepareOutputHandlingUnit(
  taskId: string, version: number, handlingUnitNumber: string | null = null
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/prepare-output`,
    { method: "POST", body: JSON.stringify({ version, handlingUnitNumber }) }
  );
}

export function reversePartialPreparationTask(
  taskId: string, reason: string, version: number
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/reverse`,
    { method: "POST", body: JSON.stringify({ reason, version }) }
  );
}

export function reportPreparationException(
  taskId: string, request: Record<string, unknown>
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/exception`,
    { method: "POST", body: JSON.stringify(request) }
  );
}

export function resolvePreparationException(
  taskId: string, request: Record<string, unknown>
): Promise<PreparationTask> {
  return authenticatedFetch<PreparationTask>(
    `/api/v1/preparations/tasks/${taskId}/exception/resolve`,
    { method: "POST", body: JSON.stringify(request) }
  );
}
